/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import { task } from 'ember-concurrency';
import { registerDestructor } from '@ember/destroyable';

import RouterService from '@ember/routing/router-service';
import Transition from '@ember/routing/-private/transition';

interface ConfirmChangesArgs {
  isDirty: boolean;
  prompt?: string;
  saveFailed: boolean;
  saveDisabled: boolean;
  status?: string;
  onSave: () => Promise<unknown>;
  onDiscard?: () => unknown;
  canDiscardChanges?: boolean;
  canSaveChanges?: boolean;
  allowedTransitions?: string[];
}

export default class ConfirmChanges extends Component<ConfirmChangesArgs> {
  @service router!: RouterService;

  @tracked pendingTransition?: Transition;

  get allowedTransitions() {
    return this.args.allowedTransitions ?? [];
  }

  confirmDiscardTask = task(async () => {
    this.args.onDiscard?.();
    this.pendingTransition?.retry();
    this.pendingTransition = undefined;
  });

  confirmChangesTask = task(async () => {
    let saveResult = await this.args.onSave();

    if (saveResult !== false) {
      this.pendingTransition?.retry();
      this.pendingTransition = undefined;
    }
  });

  constructor(owner: unknown, args: ConfirmChangesArgs) {
    super(owner, args);

    this.router.on('routeWillChange', this.onRouteWillChange);
    registerDestructor(this, () => this.router.off('routeWillChange', this.onRouteWillChange));
  }

  onRouteWillChange = (transition: Transition) => {
    // Sometimes 'routeWillChange' will be called twice for the same
    // transition, and transition on the 2nd call will have "transition.intent = undefined".
    // Causing later ".retry()" call on it to error out.
    // Test: http://127.0.0.1:4200/tests?testId=ad2e2e0f
    //
    // When that happens, let's ignore the 2nd 'routeWillChange' event by
    // returning early if "transition.intent" is not set:
    //
    // The matching 2019(!) unsolved Ember.js issue with this workaround: https://github.com/emberjs/ember.js/issues/17743
    // @ts-ignore
    if (transition.intent == null) {
      return;
    }

    let transitioningToCurrentRoute = transition.to.find((route) => route.name === this.router.currentRouteName);
    let transitioningToAllowedRoute = this.allowedTransitions.includes(transition.to.name);
    let hasPendingTransition = this.pendingTransition !== undefined;

    if (!hasPendingTransition && !transitioningToCurrentRoute && !transitioningToAllowedRoute && this.args.isDirty) {
      this.pendingTransition = transition;
      transition.abort();
    }
  };
}
